<template>
    <v-row v-resize="onResize" no-gutters class="fill-height">
        <v-col
            ref="menu"
            cols="12"
            md="3"
            sm="12"
            lg="3"
            class="border-right d-flex flex-column flex-nowrap"
            :style="`height: ${height}px;`"
        >
            <v-row no-gutters class="border-bottom flex-grow-0">
                <!-- Search -->
                <v-col class="flex-grow-1 pa-2">
                    <v-text-field
                        v-model="search"
                        clearable
                        dense
                        filled
                        hide-details="auto"
                        placeholder="Search"
                        prepend-inner-icon="mdi-magnify"
                        rounded
                    />
                </v-col>
                <v-col
                    v-if="canUploadTimesheets"
                    class="flex-grow-0 pa-1 d-flex align-center justify-end"
                >
                    <v-btn
                        depressed
                        class="mx-2"
                        color="primary"
                        rounded
                        @click="openUploadFile"
                    >
                        <v-icon>mdi-upload</v-icon>
                        {{ $vuetify.breakpoint.smAndDown ? '' : 'UPDATE' }}
                    </v-btn>
                </v-col>
            </v-row>
            <!-- Loader -->
            <div
                v-if="loading"
                class="d-flex align-center justify-center flex-grow-1"
            >
                <v-progress-circular color="primary" indeterminate />
            </div>
            <!-- External Users list -->
            <v-list v-else class="overflow-y-auto pa-0">
                <template v-if="filteredUsers.length > 0">
                    <div v-for="(user, i) in filteredUsers" :key="user.id">
                        <v-sheet
                            min-height="100"
                            class="fill-height"
                            color="transparent"
                        >
                            <v-lazy
                                :v-model="user"
                                :options="{
                                    threshold: 0.5,
                                }"
                                class="fill-height"
                            >
                                <v-list-item
                                    color="primary"
                                    three-line
                                    link
                                    @click="handleClick(user)"
                                    :class="{
                                        'border-bottom':
                                            i < filteredUsers.length - 1,
                                        'selected-item':
                                            selectedUser === user.id,
                                    }"
                                >
                                    <v-list-item-content>
                                        <v-list-item-title class="mb-2">
                                            <v-avatar
                                                color="grey lighten-4"
                                                size="34"
                                                class="mr-1"
                                            >
                                                <span class="text-h6">
                                                    {{
                                                        user.name
                                                            .split(' ')
                                                            .map((n, i, a) =>
                                                                i === 0 ||
                                                                i + 1 ===
                                                                    a.length
                                                                    ? n[0]
                                                                    : null
                                                            )
                                                            .join('')
                                                            .toUpperCase()
                                                    }}
                                                </span>
                                            </v-avatar>
                                            {{ user.name }}</v-list-item-title
                                        >
                                    </v-list-item-content>
                                    <v-btn
                                        icon
                                        class="ml-auto"
                                        aria-label="Delete External User"
                                        @click.stop="openDeleteDialog(user)"
                                    >
                                        <v-icon>mdi-trash-can</v-icon>
                                    </v-btn>
                                </v-list-item>
                            </v-lazy>
                        </v-sheet>
                    </div>
                </template>
                <v-list-item v-else>
                    <v-list-item-content>
                        <v-list-item-title class="text-center">
                            No External Users
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </v-list>
        </v-col>
        <v-col
            ref="content"
            cols="12"
            sm="0"
            md="9"
            lg="9"
            class="overflow-y-auto"
            :style="`height: ${height}px`"
        >
            <router-view :key="viewKey" @show="show" />
        </v-col>
        <!--update Form-->
        <v-dialog
            persistent
            fullscreen
            v-model="updateForm"
            content-class="update-timesheets-dialog"
        >
            <UpdateTimesheetsForm
                v-if="updateForm"
                :externalUsers="externalUsers"
                :timesheets="timesheets"
                :projectsLocations="projectsLocations"
                :projects="projects"
                @close="closeUpdateForm"
            />
        </v-dialog>
        <!--UPLOAD FILE-->
        <v-dialog v-model="openDialog" persistent max-width="400">
            <v-card :loading="loading">
                <v-card-title>
                    <v-btn
                        class="mr-3"
                        depressed
                        color="primary"
                        icon
                        rounded
                        :loading="loading"
                        @click="closeUploadFile"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    Upload File
                    <v-spacer></v-spacer>
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <v-form v-model="valid" class="mt-4">
                        <v-row no-gutters>
                            <v-file-input
                                truncate-length="15"
                                v-model="file"
                                placeholder="File *"
                                accept=".xlsx"
                                hide-details
                                required
                                class="my-2"
                                :rules="[rules.required]"
                            ></v-file-input>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        rounded
                        :loading="loading"
                        class="mb-3"
                        @click="readXLSX"
                        :disabled="!valid"
                    >
                        UPLOAD
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--UPLOAD PROJECTS LOCATION-->
        <v-dialog v-model="openProjectsLocation" persistent max-width="700">
            <v-card :loading="loading">
                <v-card-title>
                    <v-btn
                        class="mr-3"
                        depressed
                        color="primary"
                        icon
                        rounded
                        :loading="loading"
                        :disabled="loading"
                        @click="closeProjectsLocation"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    Locations
                    <v-spacer></v-spacer>
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <div class="d-flex align-right mt-3 ">
                        <strong>
                            The following locations were detected that were not
                            associated with a project.
                        </strong>
                    </div>
                    <v-data-table
                        :headers="headersLocations"
                        :items="locationsWithoutProjects"
                        :loading="loading"
                        :mobile-breakpoint="0"
                        hide-default-footer
                        disable-pagination
                        class="elevation-1 mt-4"
                    >
                        <template v-slot:item.nearby_place="{ item }">
                            <span>{{ item.nearby_place }}</span>
                        </template>

                        <template v-slot:item.project="{ item }">
                            <v-autocomplete
                                v-model="item.project"
                                :items="filteredProjects"
                                item-text="name"
                                item-value="id"
                                dense
                                hide-details
                                placeholder="Select project"
                                class="ma-0 pa-0"
                                style="max-width: 250px"
                                @change="loadProjectLocations(item)"
                            />
                        </template>
                        <template v-slot:item.location="{ item }">
                            <v-autocomplete
                                v-model="item.location"
                                :items="projectLocations[item.project] || []"
                                item-text="name"
                                item-value="id"
                                dense
                                hide-details
                                placeholder="Select Location"
                                class="ma-0 pa-0"
                                style="max-width: 250px"
                                :disabled="item.project === null"
                            />
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        rounded
                        :loading="loading"
                        class="mb-3"
                        @click="openUpdateForm"
                        :disabled="isLocationsWithoutProjectsEmpty || loading"
                    >
                        NEXT
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!--UPLOAD External users-->
        <v-dialog v-model="openExternalUsersForm" persistent max-width="700">
            <v-card :loading="loading">
                <v-card-title>
                    <v-btn
                        class="mr-3"
                        depressed
                        color="primary"
                        icon
                        rounded
                        :loading="loading"
                        :disabled="loading"
                        @click="closeExternalUsersForm"
                    >
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                    Users
                    <v-spacer></v-spacer>
                </v-card-title>
                <v-divider class="ml-4 mr-5" />
                <v-card-text>
                    <div class="d-flex align-right mt-3 ">
                        <strong>
                            The following unsaved users were detected.
                        </strong>
                    </div>
                    <v-data-table
                        :headers="headersExternalUsers"
                        :items="usersWithoutSaving"
                        :loading="loading"
                        class="elevation-1 mt-4"
                        :mobile-breakpoint="0"
                        hide-default-footer
                        disable-pagination
                    >
                        <template v-slot:item.hourlyPrice="{ item }">
                            <v-text-field
                                v-model="item.hourlyPrice"
                                :rules="[
                                    rules.required,
                                    rules.validDollarAmount,
                                ]"
                                type="number"
                                prepend-icon="mdi-currency-usd"
                                label="Hourly Price *"
                                required
                            />
                        </template>
                    </v-data-table>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        color="primary"
                        rounded
                        :loading="loading"
                        class="mb-3"
                        @click="openTimesheetsDialog"
                        :disabled="isUsersWithoutSavingEmpty || loading"
                    >
                        NEXT
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <v-dialog
            v-model="deleteDialog"
            :retain-focus="false"
            persistent
            max-width="400px"
        >
            <v-card :loading="loading">
                <v-card-title class="text-h5"
                    >Delete External User</v-card-title
                >
                <v-card-text>
                    Are you sure you want to delete this external user?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteDialog">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteExternalUser"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-row>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import UpdateTimesheetsForm from './UpdateTimesheetsForm.vue'
import * as XLSX from 'xlsx/xlsx.mjs'
import { v4 as uuidv4 } from 'uuid'
import API from '@/services/api'

export default {
    name: 'UsersTimesheets',
    components: {
        UpdateTimesheetsForm,
    },
    data() {
        return {
            user: null,
            selectedUser: null,
            viewKey: false,
            loading: false,
            height: 0,
            search: null,
            action: { active: false },
            updateForm: false,
            openDialog: false,
            openProjectsLocation: false,
            openExternalUsersForm: false,
            file: null,
            valid: false,
            rules: {
                required: v => !!v || 'invalid value',
                validDollarAmount: value =>
                    /^\d+(\.\d{1,2})?$/.test(value) ||
                    'Enter a valid amount (maximum 2 decimals)',
            },
            timesheets: [],
            headersLocations: [
                {
                    text: 'NEARBY PLACE',
                    value: 'nearby_place',
                    sortable: false,
                },
                {
                    text: 'PROJECT',
                    value: 'project',
                    sortable: false,
                },
                {
                    text: 'LOCATION',
                    value: 'location',
                    sortable: false,
                },
            ],
            headersExternalUsers: [
                {
                    text: 'ID',
                    value: 'siaId',
                    sortable: false,
                    align: 'center',
                    width: '50px',
                },
                {
                    text: 'NAME',
                    value: 'name',
                    sortable: false,
                    align: 'center',
                    width: '100px',
                },
                {
                    text: 'LAST NAME',
                    value: 'lastName',
                    sortable: false,
                    align: 'center',
                    width: '100px',
                },
                {
                    text: 'HOURLY PRICE',
                    value: 'hourlyPrice',
                    sortable: false,
                    align: 'center',
                    width: '100px',
                },
            ],
            projectsLocations: [],
            projectLocations: {},
            locationsWithoutProjects: [],
            projects: [],
            externalUsers: [],
            usersWithoutSaving: [],
            companyId: JSON.parse(localStorage.getItem('company')),
            userId: JSON.parse(localStorage.getItem('userId')),
            userToDelete: {},
            deleteDialog: false,
        }
    },
    computed: {
        ...mapState(['userRef']),
        canUploadTimesheets() {
            return this.user?.permissions?.includes('payrollManager') === true
        },
        filteredUsers() {
            return this.externalUsers.filter(q => {
                if (this.search) {
                    return q.name
                        .toLowerCase()
                        .includes(this.search.toLowerCase())
                } else {
                    return true
                }
            })
        },
        isUsersWithoutSavingEmpty() {
            if (!this.usersWithoutSaving.length) return true
            return this.usersWithoutSaving.some(item => {
                const hourlyPriceEmpty = !item.hourlyPrice

                return hourlyPriceEmpty
            })
        },
        isLocationsWithoutProjectsEmpty() {
            if (!this.locationsWithoutProjects.length) return true

            return this.locationsWithoutProjects.some(item => {
                const nearbyEmpty =
                    !item.nearby_place || item.nearby_place.trim() === ''
                const projectEmpty = !item.project
                const locationEmpty = !item.location
                return nearbyEmpty || projectEmpty || locationEmpty
            })
        },
        filteredProjects() {
            const projectIds = new Set(
                this.projectsLocations
                    .filter(pl => pl.projectId)
                    .map(pl => pl.projectId)
            )
            return this.projects.filter(p => projectIds.has(p.id))
        },
    },
    watch: {
        action: function() {
            if (this.action.active) {
                setTimeout(() => {
                    this.action.active = false
                }, 5000)
            }
        },
    },
    async mounted() {
        try {
            this.loading = true
            const {
                data: { user },
            } = await API.getMyInfo()
            this.user = user
            this.externalUsers = await API.getAllExternalUsers({
                companyId: this.companyId,
            })
            this.$root.$on('refresh-externalUsers', () => {
                this.updateExternalUsers()
            })
            await this.loadData()
        } catch (error) {
            this.setErrorItems({
                source: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        async updateExternalUsers() {
            try {
                this.loading = true
                this.externalUsers = await API.getAllExternalUsers({
                    companyId: this.companyId,
                })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async loadData() {
            try {
                this.loading = true
                this.projects = await API.getLiteProjects({ all: true })
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async openTimesheetsDialog() {
            let success = false
            try {
                this.loading = true
                const forSave = this.usersWithoutSaving.map(obj => ({
                    name: `${obj.name} ${obj.lastName}`.trim(),
                    companyId: this.companyId,
                    userId: this.userId,
                    siaId: this.normalizeSiaId(obj.siaId),
                    active: true,
                    subcontracted: obj.type !== 'Nómina',
                    hourlyPrice:
                        obj.hourlyPrice != null
                            ? String(obj.hourlyPrice)
                            : undefined,
                }))
                const USER_CHUNK = 10
                for (let i = 0; i < forSave.length; i += USER_CHUNK) {
                    const chunk = forSave.slice(i, i + USER_CHUNK)
                    await Promise.all(
                        chunk.map(obj => API.createExternalUserSIA(obj))
                    )
                }
                this.externalUsers = await API.getAllExternalUsers({
                    companyId: this.companyId,
                })
                success = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeExternalUsersForm()
                if (success) {
                    this.updateForm = true
                }
            }
        },
        async openUpdateForm() {
            let success = false
            try {
                this.loading = true
                if (this.locationsWithoutProjects.length > 0) {
                    const groupedLocations = Object.values(
                        this.locationsWithoutProjects.reduce((acc, item) => {
                            const key = item.location
                            if (!acc[key]) {
                                acc[key] = {
                                    locationId: key,
                                    projectId: item.project,
                                    nearbyPlaces: [],
                                }
                            }
                            acc[key].nearbyPlaces.push(item.nearby_place)
                            return acc
                        }, {})
                    )
                    await Promise.all(
                        groupedLocations.map(obj =>
                            API.updateProjectLocation(obj)
                        )
                    )
                    groupedLocations.forEach(groupedLoc => {
                        const existing = this.projectsLocations.find(
                            proj => proj.id === groupedLoc.locationId
                        )

                        if (existing) {
                            const combined = [
                                ...(existing.nearbyPlaces || []),
                                ...(groupedLoc.nearbyPlaces || []),
                            ]
                            const uniqueNearbyPlaces = Array.from(
                                new Set(combined.filter(Boolean))
                            )
                            existing.nearbyPlaces = uniqueNearbyPlaces
                        }
                    })
                }
                this.timesheets.forEach(person => {
                    person.timesheets.forEach(ts => {
                        const placeIn = (ts.nearby_place || '').trim()
                        const placeOut = (
                            ts.nearby_place_clock_out || ''
                        ).trim()
                        const matchIn = this.findLocationByNearbyPlace(placeIn)
                        const matchOut =
                            placeOut &&
                            placeOut.toLowerCase() !== placeIn.toLowerCase()
                                ? this.findLocationByNearbyPlace(placeOut)
                                : matchIn

                        if (matchIn) {
                            ts.clockInLocation = matchIn.location || null
                            ts.clockOutLocation =
                                (matchOut && matchOut.location) || null
                            ts.projectLocation = matchIn || null
                            ts.projectId = matchIn.projectId || null
                            if (matchOut && matchOut !== matchIn) {
                                ts.clockOutProjectLocation = matchOut || null
                                ts.clockOutProjectId =
                                    matchOut.projectId || null
                            }
                        }
                    })
                })
                this.usersWithoutSaving = this.timesheets.filter(
                    ts => !this.findExternalUserForTimesheet(ts)
                )
                success = true
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeProjectsLocation()
                if (success) {
                    if (this.usersWithoutSaving.length > 0) {
                        this.openExternalUsersForm = true
                    } else {
                        this.updateForm = true
                    }
                }
            }
        },
        closeUpdateForm() {
            this.updateForm = false
            this.projectsLocations = []
        },
        openUploadFile() {
            if (!this.canUploadTimesheets) {
                this.setErrorItems({
                    source: this.$options.name,
                    message:
                        'Only payroll managers can upload timesheet documents.',
                })
                return
            }
            this.openDialog = true
        },
        openDeleteDialog(user) {
            this.userToDelete = user
            this.deleteDialog = true
        },
        closeDeleteDialog() {
            this.deleteDialog = false
            this.userToDelete = {}
        },
        async deleteExternalUser() {
            try {
                this.loading = true
                await API.deleteGeneralExternalUser({
                    userId: this.userToDelete.id,
                })
                this.externalUsers = this.externalUsers.filter(
                    u => u.id !== this.userToDelete.id
                )
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeDeleteDialog()
            }
        },
        closeUploadFile() {
            this.openDialog = false
            this.file = null
        },
        closeProjectsLocation() {
            this.openProjectsLocation = false
            this.locationsWithoutProjects = []
            this.closeUploadFile()
        },
        closeExternalUsersForm() {
            this.openExternalUsersForm = false
            this.usersWithoutSaving = []
            this.closeProjectsLocation()
        },
        handleClick(user) {
            this.selectedUser = user.id
            this.hide(user)
        },
        onResize() {
            this.height =
                window.innerHeight -
                this.$vuetify.application.top -
                this.$vuetify.application.footer
        },
        show() {
            this.$refs.menu.classList.remove('d-none')
            this.$refs.menu.classList.add('d-flex')
        },
        hide(user) {
            if (this.$vuetify.breakpoint.smAndDown) {
                this.$refs.menu.classList.remove('d-flex')
                this.$refs.menu.classList.add('d-none')
            }
            this.viewKey = !this.viewKey
            const newRoute = {
                name: 'updateExternalUserForm',
                params: {
                    id: user.id,
                    loading: this.loading,
                    users: this.filteredUsers,
                },
            }
            if (
                this.$route.name !== newRoute.name ||
                this.$route.params.id !== newRoute.params.id
            ) {
                this.$router.push(newRoute)
            }
        },
        async readXLSX() {
            let success = false
            try {
                this.loading = true
                if (
                    this.file.name.endsWith('.xls') ||
                    this.file.name.endsWith('.xlsx')
                ) {
                    const allowedKeys = [
                        'ID',
                        'NOMBRE',
                        'APELLIDO',
                        'TIPO',
                        'FECHA',
                        'ENTRADA',
                        'SALIDA',
                        'LUGAR CERCANO',
                    ]
                    const data = await this.file.arrayBuffer()
                    const workbook = XLSX.read(data, {
                        type: 'array',
                        cellDates: true,
                    })
                    const sheetName = 'Detalle'
                    const sheet = workbook.Sheets[sheetName]
                    if (!sheet) {
                        throw new Error('Sheet "Detalle" not found')
                    }
                    const rows = XLSX.utils.sheet_to_json(sheet, {
                        header: 1,
                        raw: false,
                        defval: '',
                        blankrows: false,
                    })
                    const headerIdx = rows.findIndex(row => {
                        const normalized = (row || []).map(c =>
                            String(c || '')
                                .trim()
                                .toUpperCase()
                        )
                        return (
                            normalized.includes('ID') &&
                            normalized.includes('FECHA') &&
                            normalized.includes('ENTRADA') &&
                            normalized.includes('SALIDA')
                        )
                    })
                    if (headerIdx === -1) {
                        throw new Error(
                            'The file does not contain the required columns'
                        )
                    }
                    const headerRow = (rows[headerIdx] || []).map(c =>
                        String(c || '').trim()
                    )
                    const missingColumns = allowedKeys.filter(
                        key => !headerRow.includes(key)
                    )
                    if (missingColumns.length > 0) {
                        throw new Error(
                            `The file does not contain the required columns: ${missingColumns.join(
                                ', '
                            )}`
                        )
                    }
                    const colIndex = {}
                    headerRow.forEach((name, idx) => {
                        if (name) colIndex[name] = idx
                    })
                    const xlsData = []
                    for (let r = headerIdx + 1; r < rows.length; r++) {
                        const row = rows[r] || []
                        const get = key => {
                            const idx = colIndex[key]
                            if (idx == null) return ''
                            const val = row[idx]
                            return val == null ? '' : val
                        }
                        const idVal = get('ID')
                        if (
                            String(idVal).trim() === '' &&
                            String(get('NOMBRE')).trim() === ''
                        ) {
                            continue
                        }
                        xlsData.push({
                            ID: this.normalizeSiaId(idVal),
                            NOMBRE: String(get('NOMBRE') || '').trim(),
                            APELLIDO: String(get('APELLIDO') || '').trim(),
                            TIPO: String(get('TIPO') || '').trim(),
                            FECHA: get('FECHA'),
                            ENTRADA: get('ENTRADA'),
                            SALIDA: get('SALIDA'),
                            'LUGAR CERCANO': String(
                                get('LUGAR CERCANO') || ''
                            ).trim(),
                            'LUGAR CERCANO SALIDA': String(
                                get('LUGAR CERCANO SALIDA') || ''
                            ).trim(),
                        })
                    }
                    this.timesheets = await this.transformData(xlsData)
                    if (!this.timesheets.length) {
                        throw new Error(
                            'No timesheet rows were found in the Excel file'
                        )
                    }
                    const allNearbyPlaces = this.timesheets.flatMap(person =>
                        (person.timesheets || [])
                            .map(t => t.nearby_place?.trim())
                            .filter(Boolean)
                    )
                    const uniqueNearbyPlaces = [...new Set(allNearbyPlaces)]
                    this.projectsLocations = await API.getAllProjectLocations(
                        {}
                    )
                    this.locationsWithoutProjects = uniqueNearbyPlaces
                        .filter(place => !this.findLocationByNearbyPlace(place))
                        .map(place => ({
                            nearby_place: place,
                            project: null,
                            location: null,
                        }))

                    success = true
                }
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
                this.closeUploadFile()
                if (success) {
                    if (this.locationsWithoutProjects.length > 0) {
                        this.openProjectsLocation = true
                    } else {
                        this.openUpdateForm()
                    }
                }
            }
        },

        findLocationByNearbyPlace(place) {
            const placeKey = String(place || '')
                .trim()
                .toLowerCase()
            if (!placeKey) return null
            const exact =
                this.projectsLocations.find(
                    pl =>
                        Array.isArray(pl.nearbyPlaces) &&
                        pl.nearbyPlaces.some(
                            np =>
                                String(np || '')
                                    .trim()
                                    .toLowerCase() === placeKey
                        )
                ) || null
            if (exact) return exact
            return (
                this.projectsLocations.find(
                    pl =>
                        Array.isArray(pl.nearbyPlaces) &&
                        pl.nearbyPlaces.some(np => {
                            const npKey = String(np || '')
                                .trim()
                                .toLowerCase()
                            if (!npKey || npKey.length < 3) return false
                            return (
                                npKey.includes(placeKey) ||
                                placeKey.includes(npKey)
                            )
                        })
                ) || null
            )
        },
        normalizePersonName(name, lastName = '') {
            return `${name || ''} ${lastName || ''}`
                .trim()
                .toLowerCase()
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                .replace(/\s+/g, ' ')
        },
        findExternalUserForTimesheet(ts) {
            const bySia = this.externalUsers.find(u =>
                this.siaIdsMatch(u.siaId, ts.siaId)
            )
            if (bySia) return bySia
            const excelName = this.normalizePersonName(ts.name, ts.lastName)
            if (!excelName) return null
            return (
                this.externalUsers.find(u => {
                    const userName = this.normalizePersonName(u.name)
                    return (
                        userName === excelName ||
                        userName.includes(excelName) ||
                        excelName.includes(userName)
                    )
                }) || null
            )
        },

        getTimeMinutes(row) {
            const timeStr = String(row.ENTRADA || row.SALIDA || '').trim()
            if (!timeStr) return 0
            const parts = timeStr.match(/^(\d{1,2}):(\d{2})/)
            if (!parts) return 0
            return parseInt(parts[1], 10) * 60 + parseInt(parts[2], 10)
        },
        normalizeSiaId(id) {
            if (id == null || id === '') return null
            return String(id).trim()
        },
        siaIdsMatch(a, b) {
            const left = this.normalizeSiaId(a)
            const right = this.normalizeSiaId(b)
            if (!left || !right) return false
            if (left === right) return true
            const strip = v => v.replace(/^0+/, '') || '0'
            return strip(left) === strip(right)
        },
        transformData(data) {
            const filtered = data.filter(item => {
                const hasTime =
                    String(item.ENTRADA || '').trim() !== '' ||
                    String(item.SALIDA || '').trim() !== ''
                return hasTime && this.normalizeSiaId(item.ID)
            })
            filtered.sort((a, b) => {
                const idCompare = this.normalizeSiaId(a.ID).localeCompare(
                    this.normalizeSiaId(b.ID),
                    undefined,
                    {
                        numeric: true,
                    }
                )
                if (idCompare !== 0) return idCompare
                const dateA = this.dateToTimestamp(a.FECHA, '00:00')
                const dateB = this.dateToTimestamp(b.FECHA, '00:00')
                const secA = dateA?._seconds || 0
                const secB = dateB?._seconds || 0
                if (secA !== secB) return secA - secB
                return this.getTimeMinutes(a) - this.getTimeMinutes(b)
            })
            const combined = []
            for (let i = 0; i < filtered.length; i++) {
                const current = filtered[i]
                const next = filtered[i + 1]
                const currentId = this.normalizeSiaId(current.ID)
                const nextId = next ? this.normalizeSiaId(next.ID) : null
                if (
                    String(current.SALIDA || '').trim() === '' &&
                    next &&
                    currentId === nextId &&
                    current.FECHA === next.FECHA &&
                    String(next.ENTRADA || '').trim() !== '' &&
                    String(next.SALIDA || '').trim() === ''
                ) {
                    combined.push({
                        ...current,
                        SALIDA: next.ENTRADA,
                        LUGAR_CERCANO_SALIDA:
                            next['LUGAR CERCANO'] ||
                            next['LUGAR CERCANO SALIDA'] ||
                            null,
                    })
                    i++
                } else {
                    combined.push(current)
                }
            }
            const groups = {}
            for (const item of combined) {
                const siaId = this.normalizeSiaId(item.ID)
                const clockIn = this.dateToTimestamp(item.FECHA, item.ENTRADA)
                let clockOut = this.dateToTimestamp(item.FECHA, item.SALIDA)
                clockOut = this.subtractLunchBreak(clockIn, clockOut)

                if (!groups[siaId]) {
                    groups[siaId] = {
                        siaId,
                        name: item.NOMBRE,
                        lastName: item.APELLIDO,
                        type: item.TIPO,
                        timesheets: [],
                    }
                }
                groups[siaId].timesheets.push({
                    tempId: uuidv4(),
                    date: this.dateToTimestamp(item.FECHA, '00:00'),
                    clockIn:
                        clockIn &&
                        typeof clockIn === 'object' &&
                        '_seconds' in clockIn &&
                        !isNaN(clockIn._seconds)
                            ? clockIn
                            : null,
                    clockOut:
                        clockOut &&
                        typeof clockOut === 'object' &&
                        '_seconds' in clockOut &&
                        !isNaN(clockOut._seconds)
                            ? clockOut
                            : null,
                    nearby_place: item['LUGAR CERCANO'],
                    nearby_place_clock_out:
                        item['LUGAR CERCANO SALIDA'] ||
                        item.LUGAR_CERCANO_SALIDA ||
                        null,
                })
            }
            return Object.values(groups).filter(
                person => person.timesheets && person.timesheets.length > 0
            )
        },
        async loadProjectLocations(item) {
            try {
                this.loading = true
                const locationObj = this.locationsWithoutProjects.find(
                    loc => loc.nearby_place === item.nearby_place
                )
                if (locationObj) {
                    this.$set(locationObj, 'location', null)
                }
                if (!item.project || this.projectLocations[item.project]) return
                const locations = await this.getProjectLocations(item)
                this.$set(this.projectLocations, item.project, locations)
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        async getProjectLocations(item) {
            const locations = this.projectsLocations.filter(
                loc => loc.projectId === item.project
            )
            return locations.length ? locations : []
        },
        isValidTimestamp(ts) {
            return (
                ts &&
                typeof ts === 'object' &&
                '_seconds' in ts &&
                !isNaN(ts._seconds)
            )
        },
        subtractLunchBreak(clockIn, clockOut) {
            const lunchSeconds = 30 * 60
            if (!this.isValidTimestamp(clockOut)) return clockOut
            const adjustedSeconds = clockOut._seconds - lunchSeconds
            if (
                this.isValidTimestamp(clockIn) &&
                adjustedSeconds <= clockIn._seconds
            ) {
                return clockOut
            }
            return {
                ...clockOut,
                _seconds: adjustedSeconds,
            }
        },
        dateToTimestamp(dateStr, hourStr) {
            if (dateStr == null || hourStr == null || hourStr === '') {
                return null
            }

            let hours
            let minutes
            let seconds = 0
            if (hourStr instanceof Date && !isNaN(hourStr.getTime())) {
                hours = hourStr.getHours()
                minutes = hourStr.getMinutes()
                seconds = hourStr.getSeconds()
            } else {
                const time = String(hourStr).trim()
                const ampm = time.match(/\s*(a\.?\s*m\.?|p\.?\s*m\.?|am|pm)$/i)
                const timeMatch = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/)
                if (!timeMatch) return null
                hours = parseInt(timeMatch[1], 10)
                minutes = parseInt(timeMatch[2], 10)
                seconds = parseInt(timeMatch[3] || '0', 10)
                if (ampm && /p/i.test(ampm[1]) && hours < 12) hours += 12
                if (ampm && /a/i.test(ampm[1]) && hours === 12) hours = 0
            }

            let year
            let month
            let day

            if (dateStr instanceof Date && !isNaN(dateStr.getTime())) {
                year = dateStr.getFullYear()
                month = dateStr.getMonth()
                day = dateStr.getDate()
            } else if (typeof dateStr === 'number' && !isNaN(dateStr)) {
                const excelEpoch = Date.UTC(1899, 11, 30)
                const utc = new Date(excelEpoch + dateStr * 86400000)
                year = utc.getUTCFullYear()
                month = utc.getUTCMonth()
                day = utc.getUTCDate()
            } else {
                const raw = String(dateStr).trim()
                const monthSpanishToEnglish = {
                    ene: 0,
                    jan: 0,
                    feb: 1,
                    mar: 2,
                    abr: 3,
                    apr: 3,
                    may: 4,
                    jun: 5,
                    jul: 6,
                    ago: 7,
                    aug: 7,
                    sep: 8,
                    oct: 9,
                    nov: 10,
                    dic: 11,
                    dec: 11,
                }

                let matched = raw.match(
                    /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2,4})$/
                )
                if (matched) {
                    day = parseInt(matched[1], 10)
                    month = parseInt(matched[2], 10) - 1
                    year = parseInt(matched[3], 10)
                } else {
                    matched = raw.match(
                        /^(\d{1,2})[/.\s-]+([a-záéíóú.]+)[/.\s-]+(\d{2,4})$/i
                    )
                    if (matched) {
                        day = parseInt(matched[1], 10)
                        const monKey = matched[2]
                            .toLowerCase()
                            .replace(/\./g, '')
                            .normalize('NFD')
                            .replace(/[\u0300-\u036f]/g, '')
                            .slice(0, 3)
                        month = monthSpanishToEnglish[monKey]
                        year = parseInt(matched[3], 10)
                    } else {
                        matched = raw.match(/^(\d{4})-(\d{2})-(\d{2})/)
                        if (matched) {
                            year = parseInt(matched[1], 10)
                            month = parseInt(matched[2], 10) - 1
                            day = parseInt(matched[3], 10)
                        } else {
                            const fallback = new Date(raw)
                            if (isNaN(fallback.getTime())) return null
                            year = fallback.getFullYear()
                            month = fallback.getMonth()
                            day = fallback.getDate()
                        }
                    }
                }

                if (year < 100) {
                    year += 2000
                }
                if (
                    month == null ||
                    isNaN(month) ||
                    isNaN(day) ||
                    isNaN(year)
                ) {
                    return null
                }
            }

            const date = new Date(year, month, day, hours, minutes, seconds, 0)
            if (isNaN(date.getTime())) return null
            return {
                _seconds: Math.floor(date.getTime() / 1000),
                _nanoseconds: (date.getTime() % 1000) * 1e6,
            }
        },
    },
}
</script>

<style scoped>
.border-right {
    border-width: 0 1px 0 0;
    border-style: solid;
    border-color: #f0f0f0;
}
.border-bottom {
    border-width: 0 0 1px 0;
    border-style: solid;
    border-color: #f0f0f0;
}
.selected-item {
    background-color: rgba(0, 123, 255, 0.1); /* Fondo azul claro */
    border-left: 4px solid #007bff; /* Línea de selección */
}
</style>
